import {
  Control,
  Controller,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFormGetValues,
  UseFormTrigger,
} from 'react-hook-form';

import { states, countries } from '../../../shared/hubspot-address-utils';
import { HubSpotContact } from '../../../api/billing-types';

import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';

interface HubspotContactAddressFormProps {
  control: Control<Partial<HubSpotContact>>;
  errors: FieldErrors<Partial<HubSpotContact>>;
  watch: UseFormWatch<Partial<HubSpotContact>>;
  setValue: UseFormSetValue<Partial<HubSpotContact>>;
  getValues: UseFormGetValues<Partial<HubSpotContact>>;
  trigger: UseFormTrigger<Partial<HubSpotContact>>;
}

export default function HubspotContactAddressForm({
  control,
  errors,
  watch,
  setValue,
  getValues,
  trigger,
}: HubspotContactAddressFormProps) {
  const selectedCountry = watch('properties.country') || '';

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }} sx={{ mb: -1 }}>
        <Typography variant="body1">Billing Address</Typography>
      </Grid>
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Controller
          name={'properties.address'}
          control={control}
          rules={{
            required: `Please provide a street address.`,
          }}
          render={({ field }) => (
            <TextField
              {...field}
              onBlur={() => {
                trigger('properties.address');
              }}
              label="Address Line 1"
              fullWidth
              required
              autoComplete="address-line1"
              error={!!errors.properties?.address}
              helperText={
                errors.properties?.address
                  ? errors.properties?.address.message
                  : ' '
              }
            />
          )}
        />
      </Grid>

      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Controller
          name={'properties.street_address_2'}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onBlur={() => {
                trigger('properties.street_address_2');
              }}
              label="Address Line 2"
              fullWidth
              autoComplete="address-line2"
            />
          )}
        />
      </Grid>

      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Controller
          name={'properties.city'}
          control={control}
          rules={{
            required: `Please provide a city.`,
          }}
          render={({ field }) => (
            <TextField
              {...field}
              onBlur={() => {
                trigger('properties.city');
              }}
              label="City"
              fullWidth
              required
              autoComplete="address-level2"
              error={!!errors.properties?.city}
              helperText={
                errors.properties?.city ? errors.properties?.city.message : ' '
              }
            />
          )}
        />
      </Grid>

      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Controller
          name="properties.state"
          control={control}
          rules={{
            required:
              selectedCountry === 'US'
                ? 'State is required'
                : 'Province is required',
            validate: (value) => {
              if (!value) return true;
              if (selectedCountry === 'US') {
                return (
                  states.some((state) => state.abbreviation === value) ||
                  'Please select a valid state'
                );
              }
              return true;
            },
          }}
          render={({ field }) =>
            selectedCountry === 'US' ? (
              <TextField
                {...field}
                label="State"
                fullWidth
                autoComplete="state"
                onBlur={() => {
                  trigger('properties.state');
                }}
                select
                slotProps={{
                  select: {
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 450,
                        },
                      },
                    },
                  },
                }}
                required
                error={!!errors.properties?.state}
                helperText={errors.properties?.state?.message || ' '}
              >
                {states.map((state) => (
                  <MenuItem key={state.abbreviation} value={state.abbreviation}>
                    {state.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                {...field}
                label="State / Province"
                fullWidth
                autoComplete="province"
                required
                error={!!errors.properties?.state}
                helperText={errors.properties?.state?.message || ' '}
              />
            )
          }
        />
      </Grid>

      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Controller
          name={'properties.zip'}
          control={control}
          rules={{
            required: `Please provide a zip code.`,
          }}
          render={({ field }) => (
            <TextField
              {...field}
              onBlur={() => {
                trigger('properties.zip');
              }}
              label="Zip / Postal Code"
              fullWidth
              required
              error={!!errors.properties?.zip}
              helperText={
                errors.properties?.zip ? errors.properties?.zip.message : ' '
              }
              autoComplete="postal-code"
            />
          )}
        />
      </Grid>

      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Controller
          name={'properties.country'}
          control={control}
          rules={{
            required: `Please provide a country.`,
          }}
          render={({ field }) => (
            <TextField
              {...field}
              onBlur={() => {
                trigger('properties.country');
              }}
              onChange={(e) => {
                field.onChange(e);

                if (
                  e.target.value === 'US' ||
                  getValues('properties.country') === 'US'
                ) {
                  setValue('properties.state', '');
                }
              }}
              label="Country/Region"
              fullWidth
              required
              error={!!errors.properties?.country}
              helperText={
                errors.properties?.country
                  ? errors.properties?.country.message
                  : ' '
              }
              autoComplete="country"
              select
              slotProps={{
                select: {
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 450,
                      },
                    },
                  },
                },
              }}
            >
              {countries.map((country) => (
                <MenuItem
                  key={country.abbreviation}
                  value={country.abbreviation}
                >
                  {country.displayValue}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>
    </Grid>
  );
}
